import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ComposedChart, Area, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis
} from 'recharts';
import { billingService } from '../services/api';
import toast from 'react-hot-toast';
import * as Icons from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const BillingTrendsTab = () => {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    label: 'Mes Actual'
  });

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingService.getTrends({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      if (response.success) {
        setTrends(response.data);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Error al cargar analítica avanzada');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const handleRangeChange = (range) => {
    let start, end, label;
    const now = new Date();

    switch (range) {
      case 'currentMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        label = 'Mes Actual';
        break;
      case 'lastMonth':
        const lastMonth = subDays(startOfMonth(now), 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        label = 'Mes Pasado';
        break;
      case 'last30':
        start = subDays(now, 30);
        end = now;
        label = 'Últimos 30 días';
        break;
      default:
        return;
    }

    setDateRange({
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      label
    });
  };

  if (loading && !trends) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 font-medium animate-pulse">Generando tablero de indicadores...</p>
      </div>
    );
  }

  if (!trends) return null;

  const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Calcular KPIs rápidos
  const totalPeriodAmount = trends.billerPerformance?.reduce((sum, b) => sum + parseFloat(b.total_amount), 0) || 0;
  const totalPeriodOrders = trends.billerPerformance?.reduce((sum, b) => sum + parseInt(b.order_count), 0) || 0;
  const avgTicket = totalPeriodOrders > 0 ? totalPeriodAmount / totalPeriodOrders : 0;

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50/50">
      
      {/* Top Bar - Filters */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Icons.BarChart3 className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Dashboard de Gestión</h1>
            <p className="text-xs text-slate-500">Analizando periodo: <span className="font-semibold text-indigo-600">{dateRange.label}</span> ({dateRange.startDate} - {dateRange.endDate})</p>
          </div>
        </div>

        <div className="flex items-center bg-slate-100 p-1 rounded-xl">
          {[
            { id: 'currentMonth', label: 'Este Mes' },
            { id: 'lastMonth', label: 'Mes Pasado' },
            { id: 'last30', label: '30 Días' }
          ].map((r) => (
            <button
              key={r.id}
              onClick={() => handleRangeChange(r.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                dateRange.label.toLowerCase().includes(r.label.toLowerCase())
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-6">
        
        {/* 1. KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard 
            title="Venta Total" 
            value={`$${totalPeriodAmount.toLocaleString('es-CO')}`} 
            sub="En el periodo"
            icon={<Icons.DollarSign className="w-6 h-6 text-emerald-600" />}
            color="emerald"
          />
          <KPICard 
            title="Pedidos" 
            value={totalPeriodOrders} 
            sub="Facturados"
            icon={<Icons.ShoppingBag className="w-6 h-6 text-indigo-600" />}
            color="indigo"
          />
          <KPICard 
            title="Ticket Promedio" 
            value={`$${avgTicket.toLocaleString('es-CO', { maximumFractionDigits: 0 })}`} 
            sub="Por pedido"
            icon={<Icons.ChevronUp className="w-6 h-6 text-amber-600" />}
            color="amber"
          />
          <KPICard 
            title="Sugerencia Fuga" 
            value={trends.churnRisk?.length || 0} 
            sub="Clientes"
            icon={<Icons.UserX className="w-6 h-6 text-rose-600" />}
            color="rose"
          />
        </div>

        {/* 2. Product Strategic Matrix (NEW POSITION - TOP) */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Icons.LayoutGrid className="w-5 h-5 text-indigo-500" />
            Matriz de Desempeño Estratégico (Segmentación)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuadrantBox 
              title="Estrellas" 
              icon="🌟" 
              color="emerald"
              desc="Ganan mucho y rotan rápido" 
              items={trends.productPerformance.filter(p => parseFloat(p.avg_margin) >= 25 && p.total_qty >= 300)} 
              onItemClick={setSelectedProduct}
            />
            <QuadrantBox 
              title="Caballos de Batalla" 
              icon="🐎" 
              color="indigo"
              desc="Alta rotación, margen ajustado" 
              items={trends.productPerformance.filter(p => parseFloat(p.avg_margin) < 25 && p.total_qty >= 300)} 
              onItemClick={setSelectedProduct}
            />
            <QuadrantBox 
              title="Especialidades" 
              icon="💎" 
              color="amber"
              desc="Poca rotación, gran margen" 
              items={trends.productPerformance.filter(p => parseFloat(p.avg_margin) >= 25 && p.total_qty < 300)} 
              onItemClick={setSelectedProduct}
            />
            <QuadrantBox 
              title="Críticos" 
              icon="⚠️" 
              color="rose"
              desc="Bajo margen y baja rotación" 
              items={trends.productPerformance.filter(p => parseFloat(p.avg_margin) < 25 && p.total_qty < 300)} 
              onItemClick={setSelectedProduct}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* 2. Biller Performance Leaderboard */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Icons.Trophy className="w-5 h-5 text-amber-500" />
                Ranking de Facturadores
              </h3>
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.billerPerformance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                  <XAxis type="number" hide />
                  <YAxis dataKey="user_name" type="category" width={100} fontSize={12} stroke="#64748b" />
                  <Tooltip 
                    cursor={{fill: '#f8fafc'}}
                    formatter={(val) => [`$${val.toLocaleString()}`, 'Venta Total']}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="total_amount" fill="#6366f1" radius={[0, 6, 6, 0]} barSize={25} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 3. Delivery Channels Distribution */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Icons.Truck className="w-5 h-5 text-sky-500" />
              Canales de Despacho
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={trends.deliveryChannels}
                    dataKey="total_orders"
                    nameKey="method"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                  >
                    {trends.deliveryChannels.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* 4. Customer Segmentation by Category */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Icons.Users className="w-5 h-5 text-violet-500" />
              Segmentación por Categoría
            </h3>
            <div className="space-y-4">
              {trends.customerSegmentation.map((cat, i) => (
                <div key={i} className="group cursor-default">
                  <div className="flex justify-between items-end mb-1.5">
                    <span className="text-sm font-medium text-slate-600">{cat.category_name}</span>
                    <span className="text-xs font-bold text-slate-800">${(cat.total_amount / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
                      style={{ width: `${(cat.total_amount / trends.customerSegmentation[0].total_amount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 5. Peligro de Fuga (Churn Panel) */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Icons.Zap className="w-5 h-5 text-rose-500" />
                Alertas de Retención (Churn)
              </h3>
              <span className="px-2 py-1 bg-rose-50 text-rose-600 rounded text-[10px] font-bold uppercase tracking-wider animate-pulse">Crítico</span>
            </div>
            <div className="space-y-3 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
              {trends.churnRisk.map((client, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors border border-transparent hover:border-slate-200">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-700 truncate max-w-[180px]">{client.commercial_name}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Icons.Clock className="w-3 h-3" /> hace {client.days_inactive} días
                    </span>
                  </div>
                  <button 
                    onClick={() => {
                      const msg = encodeURIComponent(`Hola ${client.commercial_name}, te saludamos de Topping Frozen. ¡Hace tiempo no sabemos de ti! Queríamos saber si necesitas algo para tu negocio.`);
                      window.open(`https://wa.me/57${client.phone}?text=${msg}`, '_blank');
                    }}
                    className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-all"
                    title="Reactivar por WhatsApp"
                  >
                    <Icons.MessageSquare className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {trends.churnRisk.length === 0 && (
                <div className="text-center py-10">
                  <Icons.ShieldCheck className="w-12 h-12 text-emerald-300 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm italic">Todos los clientes activos</p>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* 6. Product Intelligence Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Top Products - Margin vs Volume */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
             <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Icons.Target className="w-5 h-5 text-indigo-500" />
                  Inteligencia de Producto (Venta vs Rentabilidad)
                </h3>
             </div>
             <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" dataKey="total_qty" name="Volumen (Und)" label={{ value: 'Volumen de Venta', position: 'insideBottom', offset: -10 }} />
                    <YAxis type="number" dataKey="avg_margin" name="Margen (%)" unit="%" label={{ value: 'Margen Promedio', angle: -90, position: 'insideLeft' }} />
                    <ZAxis type="number" dataKey="total_profit" range={[60, 400]} name="Profit Total" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomProductTooltip />} />
                    <Scatter name="Productos" data={trends.productPerformance} fill="#6366f1" onClick={(e) => e && e.payload && setSelectedProduct(e.payload)}>
                      {trends.productPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cursor="pointer" />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
             </div>

              <div className="h-4"></div>
           </div>

          {/* Operations & Inventory Panel */}
          <div className="space-y-6">
            
            {/* Low Stock Bestsellers */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-red-100">
              <h4 className="text-xs font-black text-rose-600 uppercase tracking-[0.1em] mb-4 flex items-center gap-2">
                <Icons.AlertTriangle className="w-4 h-4" />
                Quiebre Stock (Alta Venta)
              </h4>
              <div className="space-y-3">
                {trends.lowStockBestsellers.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex justify-between items-center text-sm cursor-pointer hover:bg-rose-50/50 p-1 rounded-lg transition-colors" onClick={() => setSelectedProduct(trends.productPerformance.find(pp => pp.product_code === p.code) || p)}>
                    <div className="flex flex-col">
                      <span className="text-slate-700 font-medium leading-normal">{p.name}</span>
                      <span className="text-[10px] text-slate-400">Vendido: {parseInt(p.recent_sales)} u</span>
                    </div>
                    <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[10px] font-bold rounded border border-rose-100 whitespace-nowrap ml-2">
                      Quedan {p.stock_quantity}
                    </span>
                  </div>
                ))}
                {trends.lowStockBestsellers.length === 0 && (
                  <p className="text-xs text-slate-400 italic text-center py-4">Inventario óptimo</p>
                )}
              </div>
            </div>

            {/* Stuck Products */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
              <h4 className="text-xs font-black text-slate-500 uppercase tracking-[0.1em] mb-4 flex items-center gap-2">
                <Icons.PauseCircle className="w-4 h-4" />
                Sin Rotación (Impulso)
              </h4>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {trends.stuckProducts.map((p, i) => (
                  <div key={i} className="flex justify-between items-start text-sm border-b border-slate-50 pb-2 last:border-0 group cursor-pointer hover:bg-slate-50 p-1 rounded-lg transition-colors" onClick={() => setSelectedProduct({ ...p, product_name: p.name, product_code: p.code, current_stock: p.stock_quantity, avg_margin: 0, total_qty: 0 })}>
                    <div className="flex flex-col flex-1">
                      <span className="text-slate-700 font-medium leading-tight group-hover:text-amber-600 transition-colors uppercase text-[11px]">{p.name}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{p.code}</span>
                    </div>
                    <div className="flex flex-col items-end ml-4">
                      <span className="text-xs font-black text-slate-800">{p.stock_quantity} u</span>
                      <span className="text-[9px] text-rose-400 uppercase font-bold whitespace-nowrap">Sin Ventas</span>
                    </div>
                  </div>
                ))}
                {trends.stuckProducts.length === 0 && (
                  <p className="text-xs text-slate-400 italic text-center py-4">Todo rotando correctamente</p>
                )}
              </div>
            </div>

          </div>

        </div>

      </div>
      <div className="pb-16" />

      {/* Product Detail Modal (NEW UI) */}
      <ProductDetailModal 
        product={selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
      />
    </div>
  );
};

const QuadrantBox = ({ title, icon, color, desc, items, onItemClick }) => {
  const colorMap = {
    emerald: 'border-emerald-100 bg-emerald-50/30 text-emerald-700',
    indigo: 'border-indigo-100 bg-indigo-50/30 text-indigo-700',
    amber: 'border-amber-100 bg-amber-50/30 text-amber-700',
    rose: 'border-rose-100 bg-rose-50/30 text-rose-700'
  };

  return (
    <div className={`p-4 rounded-xl border ${colorMap[color]} flex flex-col h-full shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-[10px] font-black uppercase tracking-widest">{title}</span>
      </div>
      <p className="text-[10px] font-medium opacity-70 mb-3 leading-tight">{desc}</p>
      <div className="space-y-2 mt-auto">
        {items.slice(0, 4).map((item, idx) => (
          <div 
            key={idx} 
            onClick={() => onItemClick(item)}
            className="group flex justify-between items-center text-[10px] bg-white/60 hover:bg-white p-2 rounded-lg border border-white/50 cursor-pointer transition-all hover:shadow-md"
          >
            <span className="truncate flex-1 font-bold pr-2">{item.product_name}</span>
            <span className="font-black opacity-60">{parseFloat(item.avg_margin).toFixed(0)}%</span>
          </div>
        ))}
        {items.length > 4 && (
          <div className="text-[9px] text-center font-bold opacity-40 mt-1">+{items.length - 4} más disponibles</div>
        )}
        {items.length === 0 && (
          <div className="text-[9px] text-center italic opacity-30 py-4">Sin datos este periodo</div>
        )}
      </div>
    </div>
  );
};

const ProductDetailModal = ({ product, onClose }) => {
  if (!product) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="text-lg font-black text-slate-800 uppercase leading-snug">{product.product_name || product.name}</h2>
            <p className="text-xs font-mono text-indigo-600 font-bold">{product.product_code || product.code}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full transition-colors"
          >
            <Icons.X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <span className="text-[10px] text-slate-400 uppercase font-black block mb-1">Volumen Vendido</span>
              <span className="text-2xl font-black text-slate-800">{(product.total_qty || 0).toLocaleString()} u</span>
            </div>
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <span className="text-[10px] text-slate-400 uppercase font-black block mb-1">Stock Actual</span>
              <span className={`text-2xl font-black ${product.current_stock < 10 ? 'text-rose-500' : 'text-emerald-500'}`}>
                {(product.current_stock || 0).toLocaleString()} u
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500 font-medium">Margen Operativo</span>
              <span className={`text-lg font-black ${parseFloat(product.avg_margin) > 25 ? 'text-emerald-600' : 'text-amber-500'}`}>
                {parseFloat(product.avg_margin || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500 font-medium">Utilidad Generada</span>
              <span className="text-lg font-black text-indigo-600">
                ${parseFloat(product.total_profit || 0).toLocaleString('es-CO')}
              </span>
            </div>
            {product.purchasing_price && (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-slate-500 font-medium">Costo Maestro</span>
                <span className="text-sm font-bold text-slate-700">
                  ${parseFloat(product.purchasing_price).toLocaleString('es-CO')}
                </span>
              </div>
            )}
          </div>

          <div className="pt-4">
            <button 
              onClick={onClose}
              className="w-full py-4 bg-slate-800 text-white font-black rounded-2xl hover:bg-slate-900 transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
            >
              CerrAR DETALLE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const KPICard = ({ title, value, sub, icon, color }) => {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100'
  };

  return (
    <div className={`bg-white rounded-2xl p-5 border shadow-sm ${colorMap[color] || ''} transition-transform hover:scale-[1.02] cursor-default`}>
      <div className="flex items-center justify-between mb-3">
        {icon}
        <Icons.MoreHorizontal className="w-5 h-5 text-slate-300" />
      </div>
      <p className="text-2xl font-black text-slate-800">{value}</p>
      <div className="flex items-center justify-between mt-1">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-tighter">{title}</h4>
        <span className="text-[10px] font-medium text-slate-400">{sub}</span>
      </div>
    </div>
  );
};

const CustomProductTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 rounded-xl shadow-2xl border border-slate-100 min-w-[200px]">
        <h4 className="font-black text-slate-800 mb-2 border-b pb-2">{data.product_name}</h4>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Volumen:</span>
            <span className="font-bold text-slate-700">{data.total_qty} u</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Margen Prom:</span>
            <span className="font-bold text-indigo-600">{parseFloat(data.avg_margin).toFixed(1)}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Utilidad Total:</span>
            <span className="font-bold text-emerald-600">${parseFloat(data.total_profit).toLocaleString()}</span>
          </div>
          <div className="mt-2 pt-2 border-t flex justify-between text-xs">
            <span className="text-slate-400 tracking-tighter uppercase font-bold">Stock Actual:</span>
            <span className={`font-black ${data.current_stock < 10 ? 'text-rose-500' : 'text-slate-800'}`}>
              {data.current_stock}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default BillingTrendsTab;
